import React from 'react';
import { ExternalLink, Tag, Star, StarHalf, Video, Users } from 'lucide-react';
import type { Course } from '../types/course';

interface CourseCardProps {
  course: Course;
}

function RatingStars({ rating }: { rating: number }) {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const stars = [];

  for (let i = 0; i < 5; i++) {
    if (i < fullStars) {
      stars.push(
        <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
      );
    } else if (i === fullStars && hasHalfStar) {
      stars.push(
        <StarHalf key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
      );
    } else {
      stars.push(
        <Star key={i} className="w-5 h-5 text-gray-600" />
      );
    }
  }

  return <div className="flex space-x-1">{stars}</div>;
}

export function CourseCard({ course }: CourseCardProps) {
  const isPromo = course.prixPromo !== undefined && course.prixPromo < course.prix;
  const tags = course.tags || [];
  
  const calculateDiscount = () => {
    if (!isPromo || !course.prixPromo) return 0;
    return Math.round(((course.prix - course.prixPromo) / course.prix) * 100);
  };

  const formatPrice = (price: number) => {
    return `${price}€${course.isMonthly ? ' / Mois' : ''}`;
  };

  return (
    <div className="group bg-gray-800 rounded-xl shadow-2xl overflow-hidden transform transition-all duration-300 hover:scale-[1.02] hover:shadow-red-500/20 relative">
      {isPromo && (
        <div className="absolute top-4 right-4 z-10">
          <div className="bg-red-600 text-white px-4 py-2 rounded-full font-bold animate-pulse">
            EN PROMO
          </div>
        </div>
      )}
      <div className="relative h-48 overflow-hidden">
        <img
          src={course.image}
          alt={course.titre}
          className="w-full h-full object-cover transform transition-transform duration-300 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-black bg-opacity-40 transition-opacity duration-300 opacity-0 group-hover:opacity-100 flex items-center justify-center">
          <a
            href={course.lien}
            target="_blank"
            rel="noopener noreferrer"
            className="px-6 py-3 bg-red-600 text-white rounded-lg font-bold transform -translate-y-2 transition-all duration-300 group-hover:translate-y-0 hover:bg-red-700"
          >
            En savoir plus
          </a>
        </div>
      </div>
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <span className={`px-4 py-2 rounded-lg text-sm font-bold
            ${course.niveau === 'Débutant' ? 'bg-green-500/20 text-green-400' : ''}
            ${course.niveau === 'Intermédiaire' ? 'bg-yellow-500/20 text-yellow-400' : ''}
            ${course.niveau === 'Avancé' ? 'bg-red-500/20 text-red-400' : ''}
          `}>
            {course.niveau}
          </span>
          <div className="text-right">
            {isPromo ? (
              <div className="flex flex-col items-end">
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold text-red-500">
                    {formatPrice(course.prixPromo)}
                  </span>
                  <span className="text-sm font-bold bg-red-500/20 text-red-400 px-2 py-1 rounded">
                    -{calculateDiscount()}%
                  </span>
                </div>
                <span className="text-xl text-gray-400 line-through mt-1">
                  {formatPrice(course.prix)}
                </span>
              </div>
            ) : (
              <span className="text-2xl font-bold text-white">
                {formatPrice(course.prix)}
              </span>
            )}
          </div>
        </div>
        <h3 className="text-xl font-bold text-white mb-3 group-hover:text-red-400 transition-colors duration-200">
          {course.titre}
        </h3>
        <div className="mb-3">
          <RatingStars rating={course.rating} />
        </div>
        <p className="text-gray-300 mb-4">
          {course.description}
        </p>
        {tags.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-2">
            {tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400"
              >
                <Tag className="w-3 h-3 mr-1" />
                {tag}
              </span>
            ))}
          </div>
        )}
        <div className="flex gap-2 mt-2">
          {course.hasVideo && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
              <Video className="w-6 h-6 mr-1" />
              Formation vidéo
            </span>
          )}
          {course.hasLiveClasses && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
              <Users className="w-6 h-6 mr-1" />
              Classes en direct
            </span>
          )}
        </div>
        <div className="flex justify-end mt-4">
          <a
            href={course.lien}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center text-red-400 hover:text-red-300 transition-colors duration-200"
          >
            Voir le cours
            <ExternalLink className="w-4 h-4 ml-1" />
          </a>
        </div>
      </div>
    </div>
  );
}